import { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { SEC } from './SecurityTheme';
import {
  createInvestigation,
  getInvestigationTemplates,
  getClients,
  getClientUsers,
  InvestigationTemplateItem,
} from '../../services/api';
import { format, subDays } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  Search,
  CheckCircle2,
  AlertTriangle,
  Shield,
  FileSearch,
  Crosshair,
  Settings2,
  Eye,
  BarChart3,
  Check,
} from 'lucide-react';

interface WizardProps {
  onClose: () => void;
  onCreated: () => void;
}

const STEPS = ['Template', 'Client & Users', 'Configuration', 'Review & Launch'];

const STEP_ICONS = [Shield, Crosshair, Settings2, Eye];

interface ClientOption {
  id: string;
  companyName: string;
  tenantId?: string;
  tenantDomain?: string;
}

interface UserOption {
  userPrincipalName: string;
  displayName: string;
}

export default function InvestigationWizard({ onClose, onCreated }: WizardProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState<InvestigationTemplateItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Step 1: Template selection
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isCustom, setIsCustom] = useState(false);

  // Step 2: Client & Users
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectAllUsers, setSelectAllUsers] = useState(false);
  const [investigateAllUsers, setInvestigateAllUsers] = useState(false);

  // Step 3: Configuration
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dateRangeStart, setDateRangeStart] = useState(format(subDays(new Date(), 90), 'yyyy-MM-dd'));
  const [dateRangeEnd, setDateRangeEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [knownAttackerIPs, setKnownAttackerIPs] = useState('');
  const [expectedCountries, setExpectedCountries] = useState('US');

  // Step 4: Launch
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [notifyOnComplete, setNotifyOnComplete] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(user?.username || '');

  // Load templates on mount
  useEffect(() => {
    getInvestigationTemplates()
      .then(resp => {
        if (resp.success) setTemplates(resp.templates);
      })
      .catch(() => {})
      .finally(() => setLoadingTemplates(false));
  }, []);

  // Load clients on step 2
  useEffect(() => {
    if (step === 1 && clients.length === 0) {
      setLoadingClients(true);
      getClients({ page: 1, pageSize: 200 })
        .then(resp => {
          const opts = resp.items
            .filter((c: any) => c.tenantId)
            .map((c: any) => ({
              id: c.id,
              companyName: c.companyName,
              tenantId: c.tenantId,
              tenantDomain: c.tenantDomain,
            }));
          setClients(opts);
        })
        .catch(() => {})
        .finally(() => setLoadingClients(false));
    }
  }, [step]);

  // Load users when client selected
  useEffect(() => {
    if (selectedClient) {
      setLoadingUsers(true);
      setUsers([]);
      setSelectedUsers([]);
      setSelectAllUsers(false);
      getClientUsers(selectedClient.id)
        .then(resp => {
          if (resp.users) {
            setUsers(resp.users.map((u: any) => ({
              userPrincipalName: u.userPrincipalName,
              displayName: u.displayName || u.userPrincipalName,
            })));
          }
        })
        .catch(() => {})
        .finally(() => setLoadingUsers(false));
    }
  }, [selectedClient]);

  // Update title when template selected
  useEffect(() => {
    if (selectedTemplateId && selectedClient) {
      const tpl = templates.find(t => t.id === selectedTemplateId);
      if (tpl) {
        setTitle(`${tpl.name} - ${selectedClient.companyName}`);
        setDescription(tpl.description);
        const days = tpl.defaultDateRangeDays;
        setDateRangeStart(format(subDays(new Date(), days), 'yyyy-MM-dd'));
      }
    }
    // Admin audit should always investigate all users to discover who has admin roles
    if (selectedTemplateId === 'admin_audit') {
      setInvestigateAllUsers(true);
      setSelectedUsers([]);
    }
  }, [selectedTemplateId, selectedClient]);

  // Toggle select all users
  useEffect(() => {
    if (selectAllUsers) {
      setSelectedUsers(users.map(u => u.userPrincipalName));
    }
  }, [selectAllUsers, users]);

  const [showComparison, setShowComparison] = useState(false);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const canProceed = (): boolean => {
    switch (step) {
      case 0: return isCustom || !!selectedTemplateId;
      case 1: return !!selectedClient && (selectedTemplateId === 'admin_audit' || investigateAllUsers || selectedUsers.length > 0);
      case 2: return !!title && !!dateRangeStart && !!dateRangeEnd;
      case 3: return true;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const ips = knownAttackerIPs.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
      const countries = expectedCountries.split(/[\n,]/).map(s => s.trim()).filter(Boolean);

      const resp = await createInvestigation({
        clientId: selectedClient!.id,
        templateId: isCustom ? undefined : selectedTemplateId || undefined,
        title,
        description,
        targetUsers: investigateAllUsers ? [] : selectedUsers,
        dateRangeStart: new Date(dateRangeStart).toISOString(),
        dateRangeEnd: new Date(dateRangeEnd).toISOString(),
        dataSources: selectedTemplate?.dataSources,
        iocCategories: selectedTemplate?.iocCategories,
        knownAttackerIPs: ips,
        expectedCountries: countries.length > 0 ? countries : ['US'],
        notifyEmail: notifyOnComplete && notifyEmail ? notifyEmail : undefined,
      });

      if (resp.success) {
        setSubmitSuccess(true);
        setTimeout(() => onCreated(), 1500);
      } else {
        setSubmitError((resp as any).error || 'Failed to create investigation');
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create investigation');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered clients
  const filteredClients = clients.filter(c =>
    c.companyName.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.tenantDomain || '').toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Filtered users
  const filteredUsers = users.filter(u =>
    u.userPrincipalName.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.displayName.toLowerCase().includes(userSearch.toLowerCase())
  );

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px',
    background: SEC.panel2, border: `1px solid ${SEC.border}`,
    borderRadius: 6, color: SEC.t1, fontSize: 13, outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: SEC.t2, textTransform: 'uppercase' as const,
    letterSpacing: '0.05em', marginBottom: 4,
  };

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderBottom: `1px solid ${SEC.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileSearch style={{ color: SEC.cyan }} size={20} />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: SEC.t1, margin: 0 }}>New Investigation</h2>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: SEC.t3, cursor: 'pointer', padding: 4 }}>
          <X size={18} />
        </button>
      </div>

      {/* Step indicators */}
      <div style={{ display: 'flex', padding: '16px 20px', gap: 8, borderBottom: `1px solid ${SEC.border}` }}>
        {STEPS.map((s, i) => {
          const StepIcon = STEP_ICONS[i];
          const isActive = i === step;
          const isDone = i < step;
          return (
            <div
              key={s}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 10px', borderRadius: 6,
                background: isActive ? SEC.cyanGlow : isDone ? 'rgba(34,197,94,0.08)' : SEC.panel2,
                border: `1px solid ${isActive ? SEC.cyan : isDone ? SEC.green : SEC.border}`,
                fontSize: 11, fontWeight: 600,
                color: isActive ? SEC.cyan : isDone ? SEC.green : SEC.t3,
              }}
            >
              {isDone ? (
                <CheckCircle2 size={14} />
              ) : (
                <StepIcon size={14} />
              )}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s}</span>
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div style={{ padding: '20px', minHeight: 350 }}>
        {/* Step 1: Template */}
        {step === 0 && (
          <div>
            <p style={{ color: SEC.t2, fontSize: 13, marginBottom: 16 }}>
              Select an investigation template or create a custom investigation.
            </p>
            {loadingTemplates ? (
              <div style={{ textAlign: 'center', padding: 40, color: SEC.t2 }}>
                <Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px' }} />
                Loading templates...
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
                {templates.map(tpl => (
                  <button
                    key={tpl.id}
                    onClick={() => { setSelectedTemplateId(tpl.id); setIsCustom(false); }}
                    style={{
                      textAlign: 'left', padding: '14px 16px',
                      background: selectedTemplateId === tpl.id && !isCustom ? SEC.cyanGlow : SEC.panel2,
                      border: `1px solid ${selectedTemplateId === tpl.id && !isCustom ? SEC.cyan : SEC.border}`,
                      borderRadius: 8, cursor: 'pointer', color: SEC.t1,
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{tpl.name}</div>
                    <div style={{ fontSize: 12, color: SEC.t2, marginBottom: 8 }}>{tpl.description}</div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: SEC.t3 }}>
                      <span>{tpl.defaultDateRangeDays}d range</span>
                      <span>{tpl.dataSources.length} data sources</span>
                      <span>{tpl.iocCategories.length} IOC checks</span>
                    </div>
                  </button>
                ))}
                <button
                  onClick={() => { setSelectedTemplateId(null); setIsCustom(true); }}
                  style={{
                    textAlign: 'left', padding: '14px 16px',
                    background: isCustom ? SEC.cyanGlow : SEC.panel2,
                    border: `1px solid ${isCustom ? SEC.cyan : SEC.border}`,
                    borderRadius: 8, cursor: 'pointer', color: SEC.t1,
                    borderStyle: 'dashed',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Custom Investigation</div>
                  <div style={{ fontSize: 12, color: SEC.t2 }}>Configure all data sources and IOC categories manually</div>
                </button>
              </div>
            )}

            {/* Compare Templates toggle */}
            {templates.length > 1 && !loadingTemplates && (
              <div style={{ marginTop: 16 }}>
                <button
                  onClick={() => setShowComparison(!showComparison)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 6,
                    background: showComparison ? SEC.cyanGlow : SEC.panel2,
                    border: `1px solid ${showComparison ? SEC.cyan : SEC.border}`,
                    color: showComparison ? SEC.cyan : SEC.t2,
                    cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}
                >
                  <BarChart3 size={14} />
                  {showComparison ? 'Hide Comparison' : 'Compare Templates'}
                </button>

                {showComparison && (
                  <div style={{
                    marginTop: 12, padding: '16px', borderRadius: 8,
                    background: SEC.panel2, border: `1px solid ${SEC.border}`,
                    overflowX: 'auto',
                  }}>
                    <TemplateComparisonTable templates={templates} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Client & Users */}
        {step === 1 && (
          <div>
            <p style={{ color: SEC.t2, fontSize: 13, marginBottom: 16 }}>
              Select the client and target users to investigate.
            </p>

            {/* Client selection */}
            <label style={labelStyle}>Client</label>
            {selectedClient ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: SEC.panel2, border: `1px solid ${SEC.cyan}`,
                borderRadius: 6, marginBottom: 16,
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: SEC.t1 }}>{selectedClient.companyName}</div>
                  <div style={{ fontSize: 12, color: SEC.t3, fontFamily: 'monospace' }}>{selectedClient.tenantDomain || selectedClient.tenantId}</div>
                </div>
                <button
                  onClick={() => { setSelectedClient(null); setUsers([]); setSelectedUsers([]); }}
                  style={{ background: 'none', border: 'none', color: SEC.t3, cursor: 'pointer', padding: 4 }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: SEC.t3 }} />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    style={{ ...inputStyle, paddingLeft: 30 }}
                  />
                </div>
                {loadingClients ? (
                  <div style={{ textAlign: 'center', padding: 20, color: SEC.t2 }}>
                    <Loader2 size={16} className="animate-spin" style={{ margin: '0 auto 8px' }} />
                    Loading clients...
                  </div>
                ) : (
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${SEC.border}`, borderRadius: 6 }}>
                    {filteredClients.map(c => (
                      <button
                        key={c.id}
                        onClick={() => setSelectedClient(c)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 14px', background: 'transparent',
                          border: 'none', borderBottom: `1px solid ${SEC.border}`,
                          color: SEC.t1, cursor: 'pointer', fontSize: 13,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = SEC.panel2)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <div style={{ fontWeight: 500 }}>{c.companyName}</div>
                        <div style={{ fontSize: 11, color: SEC.t3, fontFamily: 'monospace' }}>{c.tenantDomain || c.tenantId}</div>
                      </button>
                    ))}
                    {filteredClients.length === 0 && (
                      <div style={{ padding: 16, textAlign: 'center', color: SEC.t3, fontSize: 13 }}>
                        No clients with tenant ID found
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* User selection */}
            {selectedClient && (
              <>
                {/* Admin Audit: auto-discovery banner */}
                {selectedTemplateId === 'admin_audit' ? (
                  <div style={{
                    padding: '14px 16px', marginBottom: 12,
                    background: 'rgba(88,166,255,0.08)',
                    border: '1px solid rgba(88,166,255,0.3)',
                    borderRadius: 8,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: SEC.cyan, marginBottom: 4 }}>Admin Users Auto-Discovered</div>
                    <div style={{ fontSize: 12, color: SEC.t2, lineHeight: 1.5 }}>
                      The API will query the tenant's directory roles and automatically discover which users have admin access.
                      Only those users will be scanned — no need to select users manually.
                    </div>
                  </div>
                ) : (
                <>
                {/* Investigate All toggle */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', marginBottom: 12,
                  background: investigateAllUsers ? SEC.cyanGlow : SEC.panel2,
                  border: `1px solid ${investigateAllUsers ? SEC.cyan : SEC.border}`,
                  borderRadius: 6, cursor: 'pointer',
                }}
                  onClick={() => {
                    setInvestigateAllUsers(!investigateAllUsers);
                    if (!investigateAllUsers) {
                      setSelectedUsers([]);
                      setSelectAllUsers(false);
                    }
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 3,
                    border: `2px solid ${investigateAllUsers ? SEC.cyan : SEC.border}`,
                    background: investigateAllUsers ? SEC.cyan : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {investigateAllUsers && <CheckCircle2 size={10} style={{ color: '#000' }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: SEC.t1 }}>Investigate All Licensed Users</div>
                    <div style={{ fontSize: 11, color: SEC.t2 }}>Auto-discovers all licensed users in the tenant (up to 50)</div>
                  </div>
                </div>

                {!investigateAllUsers && (<>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label style={labelStyle}>Target Users ({selectedUsers.length} selected)</label>
                  <button
                    onClick={() => {
                      if (selectedUsers.length === users.length) {
                        setSelectedUsers([]);
                        setSelectAllUsers(false);
                      } else {
                        setSelectedUsers(users.map(u => u.userPrincipalName));
                        setSelectAllUsers(true);
                      }
                    }}
                    style={{
                      background: 'none', border: 'none', color: SEC.cyan,
                      cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    }}
                  >
                    {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: SEC.t3 }} />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    style={{ ...inputStyle, paddingLeft: 30 }}
                  />
                </div>
                {loadingUsers ? (
                  <div style={{ textAlign: 'center', padding: 20, color: SEC.t2 }}>
                    <Loader2 size={16} className="animate-spin" style={{ margin: '0 auto 8px' }} />
                    Loading users...
                  </div>
                ) : (
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${SEC.border}`, borderRadius: 6 }}>
                    {filteredUsers.map(u => {
                      const isSelected = selectedUsers.includes(u.userPrincipalName);
                      return (
                        <button
                          key={u.userPrincipalName}
                          onClick={() => {
                            setSelectedUsers(prev =>
                              isSelected
                                ? prev.filter(x => x !== u.userPrincipalName)
                                : [...prev, u.userPrincipalName]
                            );
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            width: '100%', textAlign: 'left',
                            padding: '8px 14px', background: isSelected ? SEC.cyanGlow : 'transparent',
                            border: 'none', borderBottom: `1px solid ${SEC.border}`,
                            color: SEC.t1, cursor: 'pointer', fontSize: 13,
                          }}
                        >
                          <div style={{
                            width: 16, height: 16, borderRadius: 3,
                            border: `2px solid ${isSelected ? SEC.cyan : SEC.border}`,
                            background: isSelected ? SEC.cyan : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {isSelected && <CheckCircle2 size={10} style={{ color: '#000' }} />}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.displayName}</div>
                            <div style={{ fontSize: 11, color: SEC.t3, fontFamily: 'monospace' }}>{u.userPrincipalName}</div>
                          </div>
                        </button>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <div style={{ padding: 16, textAlign: 'center', color: SEC.t3, fontSize: 13 }}>
                        {users.length === 0 ? 'No users found in tenant' : 'No matching users'}
                      </div>
                    )}
                  </div>
                )}
                </>)}
                </>)}
              </>
            )}
          </div>
        )}

        {/* Step 3: Configuration */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ color: SEC.t2, fontSize: 13 }}>
              Configure the investigation parameters.
            </p>

            <div>
              <label style={labelStyle}>Investigation Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., BEC Investigation - Acme Corp"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Description (optional)</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Brief description of what triggered this investigation..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' as const }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Date Range Start</label>
                <input
                  type="date"
                  value={dateRangeStart}
                  onChange={e => setDateRangeStart(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Date Range End</label>
                <input
                  type="date"
                  value={dateRangeEnd}
                  onChange={e => setDateRangeEnd(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Known Attacker IPs (one per line, optional)</label>
              <textarea
                value={knownAttackerIPs}
                onChange={e => setKnownAttackerIPs(e.target.value)}
                placeholder="192.168.1.1&#10;10.0.0.1"
                rows={3}
                style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' as const }}
              />
            </div>

            <div>
              <label style={labelStyle}>Expected Countries (comma-separated)</label>
              <input
                type="text"
                value={expectedCountries}
                onChange={e => setExpectedCountries(e.target.value)}
                placeholder="US, CA"
                style={inputStyle}
              />
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 3 && (
          <div>
            {submitSuccess ? (
              <div style={{
                textAlign: 'center', padding: 40,
              }}>
                <CheckCircle2 size={48} style={{ color: SEC.green, margin: '0 auto 16px' }} />
                <h3 style={{ color: SEC.green, fontSize: 18, marginBottom: 8 }}>Investigation Created</h3>
                <p style={{ color: SEC.t2, fontSize: 13 }}>Data collection is now in progress. You will be redirected shortly.</p>
              </div>
            ) : (
              <>
                <p style={{ color: SEC.t2, fontSize: 13, marginBottom: 16 }}>
                  Review the investigation details before launching.
                </p>

                {submitError && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 6, marginBottom: 16,
                    background: 'rgba(239,68,68,0.1)', border: `1px solid ${SEC.red}`,
                    color: SEC.red, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <AlertTriangle size={14} />
                    {submitError}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  <ReviewField label="Title" value={title} />
                  <ReviewField label="Client" value={selectedClient?.companyName || 'N/A'} />
                  <ReviewField label="Template" value={selectedTemplate?.name || 'Custom'} />
                  <ReviewField label="Target Users" value={selectedTemplateId === 'admin_audit' ? 'Auto-discover admin users from directory roles' : investigateAllUsers ? 'All licensed users (auto-discover)' : `${selectedUsers.length} user(s)`} />
                  <ReviewField label="Date Range" value={`${dateRangeStart} to ${dateRangeEnd}`} />
                  <ReviewField label="Expected Countries" value={expectedCountries || 'US'} />
                  {selectedTemplate && (
                    <>
                      <ReviewField label="Data Sources" value={`${selectedTemplate.dataSources.length} sources`} />
                      <ReviewField label="IOC Categories" value={`${selectedTemplate.iocCategories.length} checks`} />
                    </>
                  )}
                  {knownAttackerIPs && (
                    <ReviewField label="Known Attacker IPs" value={knownAttackerIPs.split(/[\n,]/).filter(Boolean).length + ' IP(s)'} />
                  )}
                </div>

                {selectedUsers.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Target Users</label>
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 6,
                      padding: 12, background: SEC.panel2,
                      border: `1px solid ${SEC.border}`, borderRadius: 6,
                    }}>
                      {selectedUsers.slice(0, 20).map(u => (
                        <span key={u} style={{
                          padding: '3px 8px', borderRadius: 4,
                          background: SEC.cyanGlow, color: SEC.cyan,
                          fontSize: 11, fontFamily: 'monospace',
                        }}>
                          {u}
                        </span>
                      ))}
                      {selectedUsers.length > 20 && (
                        <span style={{ color: SEC.t3, fontSize: 11, alignSelf: 'center' }}>
                          +{selectedUsers.length - 20} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Notify on complete */}
                <div style={{
                  padding: '12px 14px', borderRadius: 6,
                  background: SEC.panel2, border: `1px solid ${SEC.border}`,
                }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                    onClick={() => setNotifyOnComplete(!notifyOnComplete)}
                  >
                    <div style={{
                      width: 16, height: 16, borderRadius: 3,
                      border: `2px solid ${notifyOnComplete ? SEC.cyan : SEC.border}`,
                      background: notifyOnComplete ? SEC.cyan : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {notifyOnComplete && <CheckCircle2 size={10} style={{ color: '#000' }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: SEC.t1 }}>Notify me when complete</div>
                      <div style={{ fontSize: 11, color: SEC.t2 }}>Send an email when the investigation finishes</div>
                    </div>
                  </div>
                  {notifyOnComplete && (
                    <div style={{ marginTop: 10, paddingLeft: 26 }}>
                      <input
                        type="email"
                        value={notifyEmail}
                        onChange={e => setNotifyEmail(e.target.value)}
                        placeholder="your@email.com"
                        style={{
                          ...inputStyle,
                          maxWidth: 300,
                          fontSize: 12,
                          fontFamily: 'monospace',
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer buttons */}
      {!submitSuccess && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 20px', borderTop: `1px solid ${SEC.border}`,
        }}>
          <button
            onClick={() => step > 0 ? setStep(step - 1) : onClose()}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6,
              background: SEC.panel2, border: `1px solid ${SEC.border}`,
              color: SEC.t2, cursor: 'pointer', fontSize: 13,
            }}
          >
            <ChevronLeft size={14} />
            {step > 0 ? 'Back' : 'Cancel'}
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 6,
                background: canProceed() ? SEC.cyan : SEC.t3,
                border: 'none', color: '#000', cursor: canProceed() ? 'pointer' : 'not-allowed',
                fontSize: 13, fontWeight: 600, opacity: canProceed() ? 1 : 0.5,
              }}
            >
              Next
              <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 20px', borderRadius: 6,
                background: submitting ? SEC.t3 : SEC.cyan,
                border: 'none', color: '#000', cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600,
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Launching...
                </>
              ) : (
                <>
                  <Shield size={14} />
                  Launch Investigation
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const DATA_SOURCE_LABELS: Record<string, string> = {
  signInLogs: 'Sign-In Logs',
  riskDetections: 'Risk Detections',
  directoryAudits: 'Directory Audits',
  authMethods: 'Auth Methods',
  mfaRegistration: 'MFA Registration',
  inboxRules: 'Inbox Rules',
  ruleChanges: 'Rule Changes (UAL)',
  forwarding: 'Forwarding Config',
  authAudit: 'Auth Audit (UAL)',
  spOneDrive: 'SharePoint/OneDrive',
  messageTrace: 'Message Trace',
  passwordMFA: 'Password/MFA Events',
  directoryRoles: 'Admin Roles',
};

const IOC_LABELS_MAP: Record<string, string> = {
  suspicious_inbox_rule: 'Inbox Rules',
  foreign_sign_in: 'Foreign Sign-In',
  legacy_auth: 'Legacy Auth',
  risky_sign_in: 'Risky Sign-In',
  external_forwarding: 'Ext. Forwarding',
  no_mfa: 'No MFA',
  password_reset: 'Password/MFA Reset',
  known_attacker_ip: 'Known Attacker IP',
  attacker_sent_email: 'Attacker Email',
  oauth_app_consent: 'OAuth Consent',
  mass_rule_cleanup: 'Mass Rule Cleanup',
  cross_user_ip_correlation: 'Cross-User IPs',
  privileged_role: 'Privileged Roles',
  admin_role_change: 'Admin Role Change',
  conditional_access_change: 'CA Policy Change',
  suspicious_mfa_registration: 'MFA Hijack',
  mailbox_delegation_change: 'Mailbox Delegation',
  license_change: 'License Change',
  app_registration: 'App Registration',
};

const ALL_DATA_SOURCES = Object.keys(DATA_SOURCE_LABELS);
const ALL_IOC_TYPES = Object.keys(IOC_LABELS_MAP);

const TEMPLATE_RECOMMENDATIONS: Record<string, string> = {
  bec: 'Best for email compromise',
  account_takeover: 'Most comprehensive',
  phishing_response: 'Quick phishing triage',
  insider_threat: 'Data exfiltration focus',
  post_breach: 'Full post-incident audit',
  health_check: 'Routine check',
  admin_audit: 'Privileged access review',
};

function TemplateComparisonTable({ templates }: { templates: InvestigationTemplateItem[] }) {
  const cellStyle: React.CSSProperties = {
    padding: '4px 6px', textAlign: 'center', borderBottom: `1px solid ${SEC.border}`,
    fontSize: 11,
  };
  const headerCellStyle: React.CSSProperties = {
    ...cellStyle, fontWeight: 600, color: SEC.t1,
    textAlign: 'center', fontSize: 11,
    position: 'sticky' as const, top: 0, background: SEC.panel2, zIndex: 1,
    borderBottom: `2px solid ${SEC.border}`,
    maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  const rowLabelStyle: React.CSSProperties = {
    ...cellStyle, textAlign: 'left', fontWeight: 500, color: SEC.t2,
    whiteSpace: 'nowrap', paddingRight: 12,
  };
  const groupHeaderStyle: React.CSSProperties = {
    padding: '6px 6px 4px', fontSize: 10, fontWeight: 700,
    color: SEC.cyan, textTransform: 'uppercase' as const, letterSpacing: '0.08em',
    borderBottom: `1px solid ${SEC.border}`, background: `${SEC.cyan}08`,
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ ...headerCellStyle, textAlign: 'left' }}>Feature</th>
          {templates.map(t => (
            <th key={t.id} style={headerCellStyle} title={t.name}>{t.name.replace(' Investigation', '').replace(' Response', '').replace(' Cleanup', '')}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {/* Date range row */}
        <tr>
          <td style={rowLabelStyle}>Default Range</td>
          {templates.map(t => (
            <td key={t.id} style={cellStyle}>{t.defaultDateRangeDays}d</td>
          ))}
        </tr>

        {/* Data Sources group */}
        <tr><td colSpan={templates.length + 1} style={groupHeaderStyle}>Data Sources ({ALL_DATA_SOURCES.length})</td></tr>
        {ALL_DATA_SOURCES.map(ds => (
          <tr key={ds}>
            <td style={rowLabelStyle}>{DATA_SOURCE_LABELS[ds]}</td>
            {templates.map(t => (
              <td key={t.id} style={cellStyle}>
                {t.dataSources.includes(ds as any) ? (
                  <Check size={13} style={{ color: SEC.green }} />
                ) : (
                  <span style={{ color: SEC.t3 }}>&mdash;</span>
                )}
              </td>
            ))}
          </tr>
        ))}

        {/* IOC Detection group */}
        <tr><td colSpan={templates.length + 1} style={groupHeaderStyle}>IOC Detection ({ALL_IOC_TYPES.length})</td></tr>
        {ALL_IOC_TYPES.map(ioc => (
          <tr key={ioc}>
            <td style={rowLabelStyle}>{IOC_LABELS_MAP[ioc]}</td>
            {templates.map(t => (
              <td key={t.id} style={cellStyle}>
                {t.iocCategories.includes(ioc as any) ? (
                  <Check size={13} style={{ color: SEC.green }} />
                ) : (
                  <span style={{ color: SEC.t3 }}>&mdash;</span>
                )}
              </td>
            ))}
          </tr>
        ))}

        {/* Coverage summary */}
        <tr><td colSpan={templates.length + 1} style={groupHeaderStyle}>Coverage Summary</td></tr>
        <tr>
          <td style={rowLabelStyle}>Data Sources</td>
          {templates.map(t => {
            const count = t.dataSources.length;
            const pct = Math.round((count / ALL_DATA_SOURCES.length) * 100);
            return (
              <td key={t.id} style={cellStyle}>
                <div style={{ fontSize: 12, fontWeight: 600, color: pct >= 80 ? SEC.green : pct >= 50 ? SEC.amber : SEC.t2 }}>
                  {count}/{ALL_DATA_SOURCES.length}
                </div>
              </td>
            );
          })}
        </tr>
        <tr>
          <td style={rowLabelStyle}>IOC Checks</td>
          {templates.map(t => {
            const count = t.iocCategories.length;
            const pct = Math.round((count / ALL_IOC_TYPES.length) * 100);
            return (
              <td key={t.id} style={cellStyle}>
                <div style={{ fontSize: 12, fontWeight: 600, color: pct >= 80 ? SEC.green : pct >= 50 ? SEC.amber : SEC.t2 }}>
                  {count}/{ALL_IOC_TYPES.length}
                </div>
              </td>
            );
          })}
        </tr>

        {/* Recommendations */}
        <tr><td colSpan={templates.length + 1} style={groupHeaderStyle}>Recommendation</td></tr>
        <tr>
          <td style={rowLabelStyle}>Best For</td>
          {templates.map(t => (
            <td key={t.id} style={{ ...cellStyle, fontSize: 10, color: SEC.cyan, fontWeight: 500 }}>
              {TEMPLATE_RECOMMENDATIONS[t.id] || ''}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      padding: '10px 14px', background: SEC.panel2,
      border: `1px solid ${SEC.border}`, borderRadius: 6,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: SEC.t3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: SEC.t1, fontWeight: 500 }}>{value}</div>
    </div>
  );
}
